import { useEffect, useState } from 'react'

import { useEditFacility } from '../hooks/useEditFacility'
import { useLightMode } from '@/hooks/useLightMode'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Box, FacilityIcon, Flex, Modal, Select, Text } from '@hivewatch/dark'

import {
  ControlledSelect,
  ControlledTextInput,
  DarkAddressSearch,
  PinMarker,
  SkeletonModalBody,
  StaticMapBox,
} from '@/components/ui'
import { DarkEmergencyCallConfigurationStatus } from '@/features/emergency-call'
import { FacilityType, UpdateFacilityInput } from '@/graphql/generated/schemas'
import { STRING_REGEX } from '@/utils/forms/validation'
import { tVar } from '@/utils/i18n'

import { FormInputsIProps, LatLngStateIProps } from '../types/types'
import {
  facilityTypeOptions,
  getErrorMessage,
  getFacilityTimezone,
  getFacilityTypeOption,
  latRegex,
  lonRegex,
  validateLatitude,
  validateLongitude,
} from '../utils/utils'
import { DarkAddressChangeAlert } from './DarkAddressChangeAlert'

interface DarkEditFacilityModalIProps {
  facilityId: string
  isOpen: boolean
  onClose: () => void
}

/**
 * Rebrand variant of {@link EditFacilityModal} built on `@hivewatch/dark`. The
 * plain text/select fields use the dark TextInput/Select bound to
 * react-hook-form through the `ControlledTextInput` / `ControlledSelect`
 * wrappers; address autocomplete, the map, and the emergency-call status
 * callout have no dark equivalents and are kept as-is. Rendered from the dark
 * facilities table (`DarkFacilitiesTableModal`); the light table keeps the
 * legacy modal.
 */
export const DarkEditFacilityModal = ({
  isOpen,
  onClose,
  facilityId,
}: DarkEditFacilityModalIProps) => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const { handleSubmit, errors, control, formState, setValue, watch } = useForm(
    {
      mode: 'onChange',
    }
  )
  const { isValid, isSubmitting } = formState
  const [latLng, setLatLng] = useState<LatLngStateIProps | null>(null)
  const [hasAddressChanged, setHasAddressChanged] = useState(false)
  const { latitude, longitude } = watch(['latitude', 'longitude'])
  const { showSuccess, showError } = useToasts()
  const { shouldEnableUpdateFacility, shouldEnableUpdateFacilityAddress } =
    usePermissions()
  const {
    update,
    facility,
    floors,
    defaultFloor,
    isLoading,
    error,
    isEmergencyCallEnabled,
    isEmergencyCallConfigured,
  } = useEditFacility(facilityId)

  const isNormal = facility?.type === FacilityType.Normal
  const addressLabelColor =
    colorScheme === 'light' ? 'light.text.primary' : 'text.primary'

  const onSubmit: SubmitHandler<FormInputsIProps> = async (values) => {
    const type = facility?.type
    const isMarketFacility = type === FacilityType.Market
    const latitude = values.latitude ?? String(facility?.latitude ?? '')
    const longitude = values.longitude ?? String(facility?.longitude ?? '')
    const hasCoordinates =
      Number.isFinite(parseFloat(latitude)) &&
      Number.isFinite(parseFloat(longitude))
    const input: UpdateFacilityInput = {
      id: facilityId,
      name: values.name,
      shortName: values.shortName.toLocaleUpperCase(),
      type,
      ...(!isMarketFacility && {
        address: values?.address ? [values.address] : null,
        defaultFloorId: values?.defaultFloor?.value,
        emergencyNumber: values.emergencyNumber,
        ...(hasCoordinates && {
          latitude: parseFloat(latitude),
          longitude: parseFloat(longitude),
          timeZone: getFacilityTimezone(latitude, longitude),
        }),
      }),
    }
    try {
      await update(input)
      showSuccess(
        tVar(t, 'facilities:crud_success_updated_facility', {
          name: values.name,
        })
      )
      onClose()
    } catch (e) {
      showError(getErrorMessage(e, values.name, 'updating'))
    }
  }

  // Seed the map marker from the loaded facility.
  useEffect(() => {
    if (facility?.latitude && facility?.longitude) {
      setLatLng({ lat: facility.latitude, lng: facility.longitude })
    }
  }, [facility])

  // Keep the map + timezone in sync as coordinates change.
  useEffect(() => {
    if (latRegex.test(latitude) && lonRegex.test(longitude)) {
      setLatLng({ lat: parseFloat(latitude), lng: parseFloat(longitude) })
      setValue('tz', getFacilityTimezone(latitude, longitude))
    }
  }, [latitude, longitude])

  // Facility could not be loaded.
  if (error) {
    return (
      <Modal
        colorScheme={colorScheme}
        isOpen={isOpen}
        maxW='600px'
        onClose={onClose}
        onSave={onClose}
        saveButtonLabel={t('common:close')}
        size='compact'
        title={t('facilities:crud_edit_title')}
      >
        {t('facilities:crud_facility_not_found')}
      </Modal>
    )
  }

  // Loading the facility — no footer actions yet.
  if (!facility || isLoading) {
    return (
      <Modal
        bodyStyleOverrides={{ minH: 0, overflowY: 'auto' }}
        colorScheme={colorScheme}
        isOpen={isOpen}
        isSaveHidden
        maxW='600px'
        onClose={onClose}
        size='compact'
        title={t('facilities:crud_edit_title')}
      >
        <SkeletonModalBody />
      </Modal>
    )
  }

  return (
    <Modal
      bodyStyleOverrides={{ minH: 0, overflowY: 'auto' }}
      colorScheme={colorScheme}
      isOpen={isOpen}
      isSaveDisabled={!isValid || !shouldEnableUpdateFacility}
      isSaveLoading={isSubmitting}
      maxW='600px'
      onClose={onClose}
      onSave={handleSubmit(onSubmit)}
      saveButtonLabel={t('common:save_changes')}
      showCancelButton
      size='compact'
      title={t('facilities:crud_edit_title')}
    >
      <Flex direction='column' gap='16px'>
        <ControlledTextInput
          colorScheme={colorScheme}
          control={control}
          data-testid='EditFacilityModal:name'
          defaultValue={facility.name}
          errors={errors}
          label={t('common:facility_name')}
          name='name'
          placeholder={t('facilities:crud_facility_name_placeholder_edit')}
          rules={{
            required: t('facilities:crud_facility_name_required'),
            pattern: {
              value: STRING_REGEX,
              message: t('common:insert_alphanumeric_only'),
            },
          }}
          width='100%'
        />

        <ControlledTextInput
          colorScheme={colorScheme}
          control={control}
          data-testid='EditFacilityModal:shortName'
          defaultValue={facility.shortName}
          errors={errors}
          label={t('facilities:crud_short_name_label')}
          name='shortName'
          placeholder={t('facilities:crud_short_name_placeholder_edit')}
          rules={{
            required: t('facilities:crud_short_name_required'),
            maxLength: {
              value: 8,
              message: t('facilities:crud_short_name_max_length'),
            },
          }}
          width='100%'
        />

        {/* Facility type is not editable — shown for reference only. */}
        <Select
          colorScheme={colorScheme}
          isDisabled
          label={t('facilities:crud_facility_type_label')}
          onChange={() => undefined}
          options={facilityTypeOptions}
          placeholder={t('facilities:crud_facility_type_placeholder')}
          value={getFacilityTypeOption(facility) ?? null}
        />

        {isNormal && defaultFloor && (
          <ControlledSelect
            colorScheme={colorScheme}
            control={control}
            defaultValue={defaultFloor}
            label={t('facilities:crud_default_floor_label')}
            name='defaultFloor'
            options={floors}
            placeholder={t('facilities:crud_default_floor_placeholder')}
          />
        )}

        {isNormal && (
          // AddressSearch has no dark label prop, so render one to match.
          <Box>
            <Text color={addressLabelColor} mb='4px' textStyle='label'>
              {t('common:address')}
            </Text>
            <Controller
              control={control}
              defaultValue={facility.address?.join(', ')}
              name='address'
              render={({ onChange }) => (
                <DarkAddressSearch
                  colorScheme={colorScheme}
                  defaultValue={facility.address?.join(', ')}
                  disabled={!shouldEnableUpdateFacilityAddress}
                  onSelectAddress={(address) => {
                    if (address) {
                      const originalAddress = facility.address?.join(', ')
                      onChange(address.value)
                      setValue('latitude', address.latitude, {
                        shouldValidate: true,
                      })
                      setValue('longitude', address.longitude, {
                        shouldValidate: true,
                      })
                      setHasAddressChanged(address.value !== originalAddress)
                    }
                  }}
                  showTooltip
                />
              )}
              rules={{ required: t('facilities:crud_address_required') }}
            />
          </Box>
        )}

        {isNormal && shouldEnableUpdateFacilityAddress && (
          <Flex gap='12px'>
            <Box flex='1'>
              <ControlledTextInput
                colorScheme={colorScheme}
                control={control}
                defaultValue={
                  facility.latitude != null ? String(facility.latitude) : ''
                }
                errors={errors}
                label={t('facilities:crud_latitude_label')}
                name='latitude'
                placeholder={t('facilities:crud_latitude_placeholder')}
                rules={{
                  validate: (v) => validateLatitude(v, facility.type, t),
                }}
                width='100%'
              />
            </Box>
            <Box flex='1'>
              <ControlledTextInput
                colorScheme={colorScheme}
                control={control}
                defaultValue={
                  facility.longitude != null ? String(facility.longitude) : ''
                }
                errors={errors}
                label={t('facilities:crud_longitude_label')}
                name='longitude'
                placeholder={t('facilities:crud_longitude_placeholder')}
                rules={{
                  validate: (v) => validateLongitude(v, facility.type, t),
                }}
                width='100%'
              />
            </Box>
          </Flex>
        )}

        {isNormal && (
          <ControlledTextInput
            colorScheme={colorScheme}
            control={control}
            defaultValue={facility.timeZone ?? ''}
            disabled
            errors={errors}
            label={t('facilities:crud_time_zone_label')}
            name='tz'
            width='100%'
          />
        )}

        {isEmergencyCallConfigured && (
          <ControlledTextInput
            colorScheme={colorScheme}
            control={control}
            data-testid='EditFacilityModal:emergencyNumber'
            defaultValue={facility.emergencyNumber ?? ''}
            errors={errors}
            label={t('facilities:crud_emergency_number_label')}
            name='emergencyNumber'
            placeholder={t('facilities:crud_emergency_number_placeholder')}
            width='100%'
          />
        )}

        <DarkEmergencyCallConfigurationStatus
          colorScheme={colorScheme}
          isConfigured={isEmergencyCallConfigured}
          isEnabled={isEmergencyCallEnabled}
        />

        <DarkAddressChangeAlert
          colorScheme={colorScheme}
          hasAddressChanged={hasAddressChanged}
          isEmergencyCallConfigured={isEmergencyCallConfigured}
          isEmergencyCallEnabled={isEmergencyCallEnabled}
        />

        {isNormal && latLng && (
          // Mapbox GL (StaticMapBox) mis-positions its canvas inside the dark
          // Modal's `filter: drop-shadow` wrapper and overlays the footer,
          // swallowing button clicks. `pointerEvents: none` lets those clicks
          // pass through to the Cancel/Save buttons underneath.
          <Box pointerEvents='none'>
            <StaticMapBox
              height='150px'
              latitude={latLng.lat}
              longitude={latLng.lng}
            >
              <PinMarker
                Icon={FacilityIcon}
                bgGradient='linear(to-r, #1CFDE5, #07DB94)'
                borderColor='#42CD70'
                fadedPulseBoxShadow='rgba(43, 210, 180, 0)'
                pulseBoxShadow='rgba(43, 210, 180, 0.4)'
              />
            </StaticMapBox>
          </Box>
        )}
      </Flex>
    </Modal>
  )
}
